import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Archive, CheckCircle2, ClipboardList, Download, ExternalLink, History, Paperclip, Pencil, RefreshCw, RotateCcw, Trash2, UploadCloud, X } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import {
    archiveCrmSiteTask,
    createCrmSiteTaskComment,
    createCrmSiteTask,
    editCrmSiteTask,
    deleteCrmSiteTaskAttachment,
    getAssignableUsers,
    getCrmSiteTaskCounters,
    getCrmSiteTaskAttachmentDownloadUrl,
    getCrmSiteTaskAttachments,
    getCrmSiteTaskComments,
    getCrmSiteTaskEvents,
    getCrmSiteTasks,
    uploadCrmSiteTaskAttachment,
    updateCrmSiteTaskChecklist,
    updateCrmSiteTaskStatus,
} from "../services/api";

const emptyTask = {
    title: "",
    task_type: "content_check",
    body_markdown: "",
    assigned_to_username: "",
    watcher_usernames: [],
    due_at: "",
    priority: "normal",
};

const taskTypes = {
    content_check: "Проверка текста",
    layout_check: "Проверка верстки",
    bug: "Ошибка",
    feature: "Доработка",
};

const statusLabels = {
    open: "Открыта",
    in_progress: "В работе",
    blocked: "Заблокирована",
    done: "Готово",
};

const checklistLabels = {
    pending: "Не проверено",
    ok: "Ок",
    issue: "Проблема",
};

const priorityLabels = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

const dueFilterLabels = {
    overdue: "Просрочено",
    today: "Сегодня",
    week: "7 дней",
    no_due: "Без срока",
};

const attachmentMaxBytes = 25 * 1024 * 1024;
const urlRegex = /(https?:\/\/[^\s<>()]+)/g;
const urlLineRegex = /^\s*https?:\/\/[^\s<>()]+\s*$/;

function renderTextWithLinks(text) {
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
        if (/^https?:\/\//.test(part)) {
            const cleanUrl = part.replace(/[.,;)\]}]+$/, "");
            return (
                <a key={`${cleanUrl}-${index}`} href={cleanUrl} target="_blank" rel="noreferrer">
                    {cleanUrl} <ExternalLink size={12} />
                </a>
            );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

function getPreviewText(task) {
    if (!task.checklist?.length) {
        return task.body_markdown;
    }

    return task.body_markdown
        .split("\n")
        .filter((line) => !urlLineRegex.test(line))
        .join("\n")
        .trim();
}

function renderMarkdownPreview(text) {
    return text
        .split("\n")
        .filter(Boolean)
        .map((line, index) => {
            if (line.startsWith("# ")) {
                return <h3 key={index}>{renderTextWithLinks(line.slice(2))}</h3>;
            }
            if (line.startsWith("## ")) {
                return <h4 key={index}>{renderTextWithLinks(line.slice(3))}</h4>;
            }
            if (line.startsWith("- ")) {
                return <p key={index}>- {renderTextWithLinks(line.slice(2))}</p>;
            }
            return <p key={index}>{renderTextWithLinks(line)}</p>;
        });
}

function formatDateTimeLocal(value) {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 16);
}

function toApiDateTime(value) {
    return value ? new Date(value).toISOString() : null;
}

function formatDueDate(value) {
    if (!value) {
        return "Без срока";
    }
    return new Date(value).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatFileSize(bytes) {
    if (!bytes) {
        return "0 Б";
    }
    const units = ["Б", "КБ", "МБ", "ГБ"];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

const SiteTasks = () => {
    const { user } = useContext(AuthContext);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [counters, setCounters] = useState(null);
    const [taskForm, setTaskForm] = useState(emptyTask);
    const [commentForms, setCommentForms] = useState({});
    const [attachmentUploads, setAttachmentUploads] = useState({});
    const [attachmentDragTargets, setAttachmentDragTargets] = useState({});
    const [acceptingTaskIds, setAcceptingTaskIds] = useState({});
    const [expandedChecklistNotes, setExpandedChecklistNotes] = useState({});
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [assigneeFilter, setAssigneeFilter] = useState("");
    const [scopeFilter, setScopeFilter] = useState("all");
    const [dueFilter, setDueFilter] = useState("");
    const [archiveFilter, setArchiveFilter] = useState("active");
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const filteredTasks = useMemo(() => tasks, [tasks]);
    const currentUsername = user?.username || "";

    const loadTasks = useCallback(async () => {
        setIsLoading(true);
        setMessage("");
        const isMyTasksScope = scopeFilter === "mine" && currentUsername;
        const isWatchingScope = scopeFilter === "watching" && currentUsername;
        try {
            const [tasksResponse, usersResponse] = await Promise.all([
                getCrmSiteTasks({
                    include_archived: archiveFilter === "archived",
                    status_value: statusFilter || undefined,
                    task_type: typeFilter || undefined,
                    assigned_to: isMyTasksScope ? currentUsername : assigneeFilter || undefined,
                    watcher: isWatchingScope ? currentUsername : undefined,
                    due_state: dueFilter || undefined,
                }),
                getAssignableUsers(),
            ]);
            setTasks(tasksResponse.data.map((task) => ({ ...task, attachments: [], comments: [], events: [], eventsLoaded: false })));
            setUsers(usersResponse.data);
            getCrmSiteTaskCounters()
                .then((response) => setCounters(response.data))
                .catch(() => setCounters(null));
            setTaskForm((current) => ({
                ...current,
                assigned_to_username: current.assigned_to_username || usersResponse.data[0]?.username || "",
            }));
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить задачи.");
        } finally {
            setIsLoading(false);
        }
    }, [archiveFilter, assigneeFilter, currentUsername, dueFilter, scopeFilter, statusFilter, typeFilter]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const resetForm = () => {
        setEditingTaskId(null);
        setTaskForm({ ...emptyTask, assigned_to_username: users[0]?.username || "" });
    };

    const handleScopeFilterChange = (nextScope) => {
        setScopeFilter(nextScope);
        if (nextScope !== "all") {
            setAssigneeFilter("");
        }
    };

    const handleCounterFilterClick = (counter) => {
        setStatusFilter("");

        if (counter === "active") {
            setArchiveFilter("active");
            setDueFilter("");
            return;
        }

        if (counter === "archived") {
            setArchiveFilter("archived");
            setDueFilter("");
            return;
        }

        setArchiveFilter("active");
        setDueFilter(counter);
    };

    const handleSubmitTask = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = { ...taskForm, due_at: toApiDateTime(taskForm.due_at) };
        try {
            if (editingTaskId) {
                await editCrmSiteTask(editingTaskId, payload);
                setMessage("Задача обновлена.");
            } else {
                await createCrmSiteTask(payload);
                setMessage("Задача создана.");
            }
            resetForm();
            await loadTasks();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить задачу.");
        } finally {
            setIsSaving(false);
        }
    };

    const startEditTask = (task) => {
        setEditingTaskId(task.id);
        setTaskForm({
            title: task.title,
            task_type: task.task_type || "content_check",
            body_markdown: task.body_markdown,
            assigned_to_username: task.assigned_to_username,
            watcher_usernames: task.watcher_usernames || [],
            due_at: formatDateTimeLocal(task.due_at),
            priority: task.priority || "normal",
        });
    };

    const handleStatusChange = async (taskId, status) => {
        try {
            await updateCrmSiteTaskStatus(taskId, status);
            await loadTasks();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить задачу.");
        }
    };

    const handleArchiveChange = async (taskId, archived) => {
        try {
            await archiveCrmSiteTask(taskId, archived);
            await loadTasks();
            setMessage(archived ? "Работа принята и отправлена в архив." : "Задача возвращена из архива.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить архив.");
        }
    };

    const handleAcceptAllOk = async (task) => {
        setAcceptingTaskIds((current) => ({ ...current, [task.id]: true }));
        try {
            const checklist = (task.checklist || []).map((item) => ({ ...item, status: "ok" }));
            setTaskChecklist(task.id, checklist);
            await updateCrmSiteTaskChecklist(task.id, checklist);
            await updateCrmSiteTaskStatus(task.id, "done");
            await archiveCrmSiteTask(task.id, true);
            await loadTasks();
            setMessage("Все ссылки отмечены как ок, задача закрыта и отправлена в архив.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось принять задачу одним действием.");
            await loadTasks();
        } finally {
            setAcceptingTaskIds((current) => ({ ...current, [task.id]: false }));
        }
    };

    const handleChecklistChange = async (task, itemIndex, nextStatus) => {
        const checklist = patchChecklistItem(task, itemIndex, { status: nextStatus });
        setTaskChecklist(task.id, checklist);

        try {
            await updateCrmSiteTaskChecklist(task.id, checklist);
            await refreshTaskEventsIfOpen(task.id);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить проверку.");
            await loadTasks();
        }
    };

    const patchChecklistItem = (task, itemIndex, patch) => task.checklist.map((item, index) => (
        index === itemIndex ? { ...item, ...patch } : item
    ));

    const setTaskChecklist = (taskId, checklist) => {
        setTasks((currentTasks) => currentTasks.map((currentTask) => (
            currentTask.id === taskId ? { ...currentTask, checklist } : currentTask
        )));
    };

    const handleChecklistNoteChange = (task, itemIndex, note) => {
        setTasks((currentTasks) => currentTasks.map((currentTask) => (
            currentTask.id === task.id
                ? { ...currentTask, checklist: patchChecklistItem(currentTask, itemIndex, { note }) }
                : currentTask
        )));
    };

    const handleChecklistNoteSave = async (task, itemIndex, note) => {
        const currentTask = tasks.find((candidate) => candidate.id === task.id) || task;
        const checklist = patchChecklistItem(currentTask, itemIndex, { note });
        setTaskChecklist(task.id, checklist);

        try {
            await updateCrmSiteTaskChecklist(task.id, checklist);
            await refreshTaskEventsIfOpen(task.id);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить заметку к проверке.");
            await loadTasks();
        }
    };

    const checklistNoteKey = (taskId, itemIndex) => `${taskId}:${itemIndex}`;

    const toggleChecklistNote = (taskId, itemIndex) => {
        const key = checklistNoteKey(taskId, itemIndex);
        setExpandedChecklistNotes((current) => ({ ...current, [key]: !current[key] }));
    };

    const setTaskComments = (taskId, comments) => {
        setTasks((currentTasks) => currentTasks.map((currentTask) => (
            currentTask.id === taskId ? { ...currentTask, comments } : currentTask
        )));
    };

    const loadTaskComments = async (taskId) => {
        try {
            const response = await getCrmSiteTaskComments(taskId);
            setTaskComments(taskId, response.data);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить комментарии.");
        }
    };

    const handleCommentDraftChange = (taskId, body) => {
        setCommentForms((currentForms) => ({ ...currentForms, [taskId]: body }));
    };

    const handleCreateComment = async (taskId) => {
        const body = (commentForms[taskId] || "").trim();
        if (!body) {
            return;
        }

        try {
            const response = await createCrmSiteTaskComment(taskId, body);
            setTasks((currentTasks) => currentTasks.map((currentTask) => (
                currentTask.id === taskId
                    ? {
                        ...currentTask,
                        comments: [...(currentTask.comments || []), response.data],
                        comments_count: (currentTask.comments_count || 0) + 1,
                    }
                    : currentTask
            )));
            setCommentForms((currentForms) => ({ ...currentForms, [taskId]: "" }));
            await refreshTaskEventsIfOpen(taskId);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось добавить комментарий.");
        }
    };

    const setTaskAttachments = (taskId, attachments) => {
        setTasks((currentTasks) => currentTasks.map((currentTask) => (
            currentTask.id === taskId ? { ...currentTask, attachments } : currentTask
        )));
    };

    const loadTaskAttachments = async (taskId) => {
        try {
            const response = await getCrmSiteTaskAttachments(taskId);
            setTaskAttachments(taskId, response.data);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить вложения.");
        }
    };

    const setTaskEvents = (taskId, events) => {
        setTasks((currentTasks) => currentTasks.map((currentTask) => (
            currentTask.id === taskId ? { ...currentTask, events, eventsLoaded: true } : currentTask
        )));
    };

    const loadTaskEvents = async (taskId) => {
        try {
            const response = await getCrmSiteTaskEvents(taskId);
            setTaskEvents(taskId, response.data);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить историю задачи.");
        }
    };

    const refreshTaskEventsIfOpen = async (taskId) => {
        const task = tasks.find((candidate) => candidate.id === taskId);
        if (task?.events?.length > 0) {
            await loadTaskEvents(taskId);
        }
    };

    const getAttachmentUploadState = (taskId) => attachmentUploads[taskId] || { isUploading: false, items: [] };

    const setAttachmentUploadItem = (taskId, itemId, patch) => {
        setAttachmentUploads((current) => {
            const uploadState = current[taskId] || { isUploading: false, items: [] };
            return {
                ...current,
                [taskId]: {
                    ...uploadState,
                    items: uploadState.items.map((item) => (
                        item.id === itemId ? { ...item, ...patch } : item
                    )),
                },
            };
        });
    };

    const setAttachmentDragTarget = (taskId, isActive) => {
        setAttachmentDragTargets((current) => ({ ...current, [taskId]: isActive }));
    };

    const handleUploadAttachments = async (taskId, fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) {
            return;
        }

        const uploadItems = files.map((file, index) => ({
            id: `${file.name}-${file.size}-${index}`,
            name: file.name,
            size: file.size,
            status: file.size > attachmentMaxBytes ? "rejected" : "pending",
            message: file.size > attachmentMaxBytes ? "Больше 25 МБ" : "",
        }));
        const validFiles = files
            .map((file, index) => ({ file, itemId: uploadItems[index].id }))
            .filter(({ file }) => file.size <= attachmentMaxBytes);

        setAttachmentUploads((current) => ({
            ...current,
            [taskId]: { isUploading: validFiles.length > 0, items: uploadItems },
        }));

        if (!validFiles.length) {
            setMessage("Все файлы больше 25 МБ. Архивы и фото можно прикреплять, но каждый файл должен быть в лимите.");
            return;
        }

        let uploadedCount = 0;
        let failedCount = 0;

        try {
            for (const { file, itemId } of validFiles) {
                setAttachmentUploadItem(taskId, itemId, { status: "uploading", message: "Загрузка..." });
                try {
                    const response = await uploadCrmSiteTaskAttachment(taskId, file);
                    uploadedCount += 1;
                    setTasks((currentTasks) => currentTasks.map((currentTask) => (
                        currentTask.id === taskId
                            ? {
                                ...currentTask,
                                attachments: [response.data, ...(currentTask.attachments || [])],
                                attachments_count: (currentTask.attachments_count || 0) + 1,
                            }
                            : currentTask
                    )));
                    setAttachmentUploadItem(taskId, itemId, { status: "done", message: "Готово" });
                } catch (err) {
                    failedCount += 1;
                    setAttachmentUploadItem(taskId, itemId, {
                        status: "failed",
                        message: err.response?.data?.detail || "Ошибка загрузки",
                    });
                }
            }
            const rejectedCount = uploadItems.filter((item) => item.status === "rejected").length;
            setMessage(`Загружено: ${uploadedCount}. Ошибок: ${failedCount}. Пропущено по лимиту: ${rejectedCount}.`);
            await refreshTaskEventsIfOpen(taskId);
        } finally {
            setAttachmentUploads((current) => ({
                ...current,
                [taskId]: {
                    ...(current[taskId] || { items: [] }),
                    isUploading: false,
                },
            }));
        }
    };

    const handleAttachmentDrop = (event, taskId) => {
        event.preventDefault();
        event.stopPropagation();
        setAttachmentDragTarget(taskId, false);
        handleUploadAttachments(taskId, event.dataTransfer.files);
    };

    const handleDeleteAttachment = async (taskId, attachmentId) => {
        if (!window.confirm("Удалить вложение из задачи? Файл будет удален без корзины.")) {
            return;
        }
        try {
            await deleteCrmSiteTaskAttachment(taskId, attachmentId);
            setTasks((currentTasks) => currentTasks.map((currentTask) => (
                currentTask.id === taskId
                    ? {
                        ...currentTask,
                        attachments: (currentTask.attachments || []).filter((attachment) => attachment.id !== attachmentId),
                        attachments_count: Math.max((currentTask.attachments_count || 1) - 1, 0),
                    }
                    : currentTask
            )));
            await refreshTaskEventsIfOpen(taskId);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось удалить вложение.");
        }
    };

    return (
        <main className="crm-shell">
            <header className="crm-header site-tasks-header">
                <div>
                    <span className="eyebrow">Разработка</span>
                    <h1>Текущие задачи по сайту</h1>
                    <p>Создавайте задачи разных типов, открывайте карточки из чеклиста и отмечайте результат проверки.</p>
                </div>
                <button className="icon-action" onClick={loadTasks} disabled={isLoading} title="Обновить">
                    <RefreshCw size={16} />
                </button>
            </header>

            {message && <p className="call-notice">{message}</p>}

            <section className="site-tasks-layout">
                <form className="site-task-form" onSubmit={handleSubmitTask}>
                    <h2>
                        <ClipboardList size={18} />
                        {editingTaskId ? "Редактирование задачи" : "Новая задача"}
                    </h2>
                    <select
                        value={taskForm.task_type}
                        onChange={(event) => setTaskForm({ ...taskForm, task_type: event.target.value })}
                    >
                        {Object.entries(taskTypes).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <input
                        value={taskForm.title}
                        onChange={(event) => setTaskForm({ ...taskForm, title: event.target.value })}
                        placeholder="Название задачи"
                        required
                    />
                    <select
                        value={taskForm.assigned_to_username}
                        onChange={(event) => setTaskForm({ ...taskForm, assigned_to_username: event.target.value })}
                        required
                    >
                        <option value="">Исполнитель</option>
                        {users.map((user) => (
                            <option key={user.id} value={user.username}>{user.username}</option>
                        ))}
                    </select>
                    <input
                        type="datetime-local"
                        value={taskForm.due_at}
                        onChange={(event) => setTaskForm({ ...taskForm, due_at: event.target.value })}
                    />
                    <select
                        multiple
                        className="watchers-select"
                        value={taskForm.watcher_usernames}
                        onChange={(event) => setTaskForm({
                            ...taskForm,
                            watcher_usernames: Array.from(event.target.selectedOptions).map((option) => option.value),
                        })}
                    >
                        {users
                            .filter((user) => user.username !== taskForm.assigned_to_username)
                            .map((user) => (
                                <option key={user.id} value={user.username}>{user.username}</option>
                            ))}
                    </select>
                    <select
                        value={taskForm.priority}
                        onChange={(event) => setTaskForm({ ...taskForm, priority: event.target.value })}
                    >
                        {Object.entries(priorityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <textarea
                        className="markdown-input"
                        value={taskForm.body_markdown}
                        onChange={(event) => setTaskForm({ ...taskForm, body_markdown: event.target.value })}
                        placeholder={"Описание или список ссылок:\n# Проверка карточек\nhttps://example.com/product/1\nhttps://example.com/product/2"}
                        required
                    />
                    <div className="form-actions">
                        <button disabled={isSaving || users.length === 0}>
                            {editingTaskId ? "Сохранить" : "Создать задачу"}
                        </button>
                        {editingTaskId && (
                            <button type="button" className="secondary-action" onClick={resetForm}>
                                <X size={16} /> Отмена
                            </button>
                        )}
                    </div>
                </form>

                <section className="site-task-list-panel">
                    <div className="crm-section-title task-list-header">
                        <h2>Задачи</h2>
                        <div className="task-filter-row">
                            {counters && (
                                <div className="task-counters">
                                    <button
                                        className={`task-counter-button ${archiveFilter === "active" && !dueFilter ? "is-active" : ""}`}
                                        onClick={() => handleCounterFilterClick("active")}
                                        type="button"
                                    >
                                        <span className="task-counter-label">Активно</span>
                                        <strong className="task-counter-value">{counters.active}</strong>
                                    </button>
                                    <button
                                        className={`task-counter-button ${archiveFilter === "active" && dueFilter === "overdue" ? "is-active" : ""}`}
                                        onClick={() => handleCounterFilterClick("overdue")}
                                        type="button"
                                    >
                                        <span className="task-counter-label">Просрочено</span>
                                        <strong className="task-counter-value">{counters.overdue}</strong>
                                    </button>
                                    <button
                                        className={`task-counter-button ${archiveFilter === "active" && dueFilter === "today" ? "is-active" : ""}`}
                                        onClick={() => handleCounterFilterClick("today")}
                                        type="button"
                                    >
                                        <span className="task-counter-label">Сегодня</span>
                                        <strong className="task-counter-value">{counters.due_today}</strong>
                                    </button>
                                    <button
                                        className={`task-counter-button ${archiveFilter === "archived" ? "is-active" : ""}`}
                                        onClick={() => handleCounterFilterClick("archived")}
                                        type="button"
                                    >
                                        <span className="task-counter-label">Архив</span>
                                        <strong className="task-counter-value">{counters.archived}</strong>
                                    </button>
                                </div>
                            )}
                            <div className="task-scope-filter" aria-label="Быстрый фильтр задач">
                                <button
                                    className={scopeFilter === "all" ? "is-active" : ""}
                                    onClick={() => handleScopeFilterChange("all")}
                                    type="button"
                                >
                                    Все
                                </button>
                                <button
                                    className={scopeFilter === "mine" ? "is-active" : ""}
                                    disabled={!currentUsername}
                                    onClick={() => handleScopeFilterChange("mine")}
                                    type="button"
                                >
                                    Мои
                                </button>
                                <button
                                    className={scopeFilter === "watching" ? "is-active" : ""}
                                    disabled={!currentUsername}
                                    onClick={() => handleScopeFilterChange("watching")}
                                    type="button"
                                >
                                    Наблюдаю
                                </button>
                            </div>
                            <select value={archiveFilter} onChange={(event) => setArchiveFilter(event.target.value)}>
                                <option value="active">Активные</option>
                                <option value="archived">Архив</option>
                            </select>
                            <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                                <option value="">Все типы</option>
                                {Object.entries(taskTypes).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            <select
                                disabled={scopeFilter !== "all"}
                                value={assigneeFilter}
                                onChange={(event) => {
                                    setScopeFilter("all");
                                    setAssigneeFilter(event.target.value);
                                }}
                            >
                                <option value="">Все исполнители</option>
                                {users.map((user) => (
                                    <option key={user.id} value={user.username}>{user.username}</option>
                                ))}
                            </select>
                            <select value={dueFilter} onChange={(event) => setDueFilter(event.target.value)}>
                                <option value="">Все сроки</option>
                                {Object.entries(dueFilterLabels).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                                <option value="">Все статусы</option>
                                <option value="open">Открыта</option>
                                <option value="in_progress">В работе</option>
                                <option value="blocked">Заблокирована</option>
                                <option value="done">Готово</option>
                            </select>
                        </div>
                    </div>

                    {isLoading ? (
                        <p className="empty-copy">Загрузка...</p>
                    ) : filteredTasks.length === 0 ? (
                        <p className="empty-copy">Пока задач нет.</p>
                    ) : filteredTasks.map((task) => {
                        const previewText = getPreviewText(task);
                        const attachmentUploadState = getAttachmentUploadState(task.id);
                        return (
                            <article className="site-task-card" key={task.id}>
                                <div className="site-task-card-header">
                                    <div>
                                        <div className="task-badges">
                                            <span className="task-type">{taskTypes[task.task_type] || task.task_type || "Проверка текста"}</span>
                                            <span className={`task-priority is-${task.priority}`}>{priorityLabels[task.priority] || task.priority}</span>
                                            {task.archived && <span className="task-archive-badge">В архиве</span>}
                                        </div>
                                        <h2>{task.title}</h2>
                                    </div>
                                    <span className="status-pill">{statusLabels[task.status] || task.status}</span>
                                </div>
                                <div className="task-meta">
                                    <span>Кому: {task.assigned_to_username}</span>
                                    <span>Автор: {task.created_by}</span>
                                </div>
                                <div className="task-meta task-meta-secondary">
                                    <span>Срок: {formatDueDate(task.due_at)}</span>
                                    {task.watcher_usernames?.length > 0 && (
                                        <span>Наблюдают: {task.watcher_usernames.join(", ")}</span>
                                    )}
                                </div>
                                {previewText && (
                                    <div className="markdown-preview">
                                        {renderMarkdownPreview(previewText)}
                                    </div>
                                )}

                                {task.checklist?.length > 0 && (
                                    <div className="qa-checklist">
                                        <div className="qa-checklist-header">
                                            <h3>Проверка ссылок</h3>
                                            <span>
                                                {task.checklist.filter((item) => item.status === "ok").length} ок · {task.checklist.filter((item) => item.status === "issue").length} проблем · {task.checklist.length} всего
                                            </span>
                                        </div>
                                        {task.checklist.map((item, index) => {
                                            const noteKey = checklistNoteKey(task.id, index);
                                            const noteExpanded = expandedChecklistNotes[noteKey] || Boolean(item.note);
                                            return (
                                                <div className="qa-checklist-row" key={`${item.url}-${index}`}>
                                                    <div className="qa-checklist-link">
                                                        <a href={item.url} target="_blank" rel="noreferrer">
                                                            Открыть карточку <ExternalLink size={13} />
                                                        </a>
                                                        <span>{item.url}</span>
                                                    </div>
                                                    <div className="qa-checklist-actions">
                                                        {Object.entries(checklistLabels).map(([status, label]) => (
                                                            <button
                                                                className={item.status === status ? "is-selected" : ""}
                                                                key={status}
                                                                onClick={() => handleChecklistChange(task, index, status)}
                                                                type="button"
                                                            >
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <button
                                                        className={`qa-checklist-note-toggle ${noteExpanded ? "is-open" : ""}`}
                                                        type="button"
                                                        onClick={() => toggleChecklistNote(task.id, index)}
                                                    >
                                                        {noteExpanded ? "Скрыть заметку" : item.note ? "Заметка" : "Добавить заметку"}
                                                    </button>
                                                    {noteExpanded && (
                                                        <div className="qa-checklist-note-wrap">
                                                            <textarea
                                                                className="qa-checklist-note"
                                                                value={item.note || ""}
                                                                onBlur={(event) => handleChecklistNoteSave(task, index, event.target.value)}
                                                                onChange={(event) => handleChecklistNoteChange(task, index, event.target.value)}
                                                                placeholder="Что поправить или почему всё ок"
                                                                rows={2}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div
                                    className={`task-attachments ${attachmentDragTargets[task.id] ? "is-dragging" : ""}`}
                                    onDragEnter={(event) => {
                                        event.preventDefault();
                                        setAttachmentDragTarget(task.id, true);
                                    }}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        setAttachmentDragTarget(task.id, true);
                                    }}
                                    onDragLeave={(event) => {
                                        event.preventDefault();
                                        if (event.currentTarget === event.target) {
                                            setAttachmentDragTarget(task.id, false);
                                        }
                                    }}
                                    onDrop={(event) => handleAttachmentDrop(event, task.id)}
                                >
                                    <div className="task-attachments-header">
                                        <h3><Paperclip size={16} /> Вложения</h3>
                                        <button type="button" onClick={() => loadTaskAttachments(task.id)}>
                                            {task.attachments?.length ? "Обновить" : `Показать (${task.attachments_count || 0})`}
                                        </button>
                                    </div>
                                    {task.attachments?.length > 0 && (
                                        <div className="task-attachment-list">
                                            {task.attachments.map((attachment) => (
                                                <article className="task-attachment" key={attachment.id}>
                                                    <div>
                                                        <strong>{attachment.original_filename}</strong>
                                                        <span>
                                                            {formatFileSize(attachment.size_bytes)} · {attachment.uploaded_by} · {new Date(attachment.created_at).toLocaleString("ru-RU")}
                                                        </span>
                                                    </div>
                                                    <a
                                                        href={getCrmSiteTaskAttachmentDownloadUrl(task.id, attachment.id)}
                                                        rel="noreferrer"
                                                        target="_blank"
                                                    >
                                                        <Download size={15} /> Скачать
                                                    </a>
                                                    <button
                                                        aria-label="Удалить вложение"
                                                        title="Удалить вложение"
                                                        type="button"
                                                        onClick={() => handleDeleteAttachment(task.id, attachment.id)}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                    <label className="task-attachment-upload">
                                        <UploadCloud size={16} />
                                        <input
                                            disabled={attachmentUploadState.isUploading}
                                            multiple
                                            onChange={(event) => {
                                                handleUploadAttachments(task.id, event.target.files);
                                                event.target.value = "";
                                            }}
                                            type="file"
                                        />
                                        {attachmentUploadState.isUploading ? "Загружаем..." : "Перетащить или выбрать файлы"}
                                    </label>
                                    {attachmentUploadState.items.length > 0 && (
                                        <div className="task-attachment-upload-list">
                                            {attachmentUploadState.items.map((item) => (
                                                <div className={`task-attachment-upload-item is-${item.status}`} key={item.id}>
                                                    <span>{item.name}</span>
                                                    <strong>{item.message || formatFileSize(item.size)}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <small className="task-attachment-hint">До 25 МБ на файл. Можно кидать пачку фото, PDF, документы и архивы zip/rar/7z.</small>
                                </div>

                                <div className="task-comments">
                                    <div className="task-comments-header">
                                        <h3>Обсуждение</h3>
                                        <button type="button" onClick={() => loadTaskComments(task.id)}>
                                            {task.comments?.length ? "Обновить" : `Показать (${task.comments_count || 0})`}
                                        </button>
                                    </div>
                                    {task.comments?.length > 0 && (
                                        <div className="task-comment-list">
                                            {task.comments.map((comment) => (
                                                <article className="task-comment" key={comment.id}>
                                                    <strong>{comment.created_by}</strong>
                                                    <span>{new Date(comment.created_at).toLocaleString("ru-RU")}</span>
                                                    <p>{comment.body}</p>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                    <div className="task-comment-composer">
                                        <textarea
                                            value={commentForms[task.id] || ""}
                                            onChange={(event) => handleCommentDraftChange(task.id, event.target.value)}
                                            placeholder="Комментарий по задаче"
                                            rows={2}
                                        />
                                        <button type="button" onClick={() => handleCreateComment(task.id)}>
                                            Отправить
                                        </button>
                                    </div>
                                </div>

                                <div className="task-events">
                                    <div className="task-events-header">
                                        <h3><History size={16} /> История</h3>
                                        <button type="button" onClick={() => loadTaskEvents(task.id)}>
                                            {task.eventsLoaded ? "Обновить" : "Показать"}
                                        </button>
                                    </div>
                                    {task.eventsLoaded && task.events?.length === 0 && (
                                        <p className="task-events-empty">Пока нет записей по этой задаче.</p>
                                    )}
                                    {task.events?.length > 0 && (
                                        <div className="task-event-list">
                                            {task.events.map((event) => (
                                                <article className="task-event" key={event.id}>
                                                    <div>
                                                        <strong>{event.title}</strong>
                                                        <span>{event.actor_username} · {new Date(event.created_at).toLocaleString("ru-RU")}</span>
                                                    </div>
                                                    {event.body && <p>{event.body}</p>}
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="task-actions task-actions-toolbar">
                                    <div className="task-actions-primary">
                                        {!task.archived && task.checklist?.length > 0 && (
                                            <button
                                                className="primary-task-action"
                                                disabled={acceptingTaskIds[task.id]}
                                                onClick={() => handleAcceptAllOk(task)}
                                            >
                                                <CheckCircle2 size={16} /> {acceptingTaskIds[task.id] ? "Закрываем..." : "Все ссылки OK и в архив"}
                                            </button>
                                        )}
                                        {task.archived ? (
                                            <button onClick={() => handleArchiveChange(task.id, false)}><RotateCcw size={16} /> Вернуть</button>
                                        ) : (
                                            <button onClick={() => handleArchiveChange(task.id, true)}><Archive size={16} /> Принять в архив</button>
                                        )}
                                    </div>
                                    <div className="task-actions-secondary">
                                        <label>
                                            Статус
                                            <select value={task.status} onChange={(event) => handleStatusChange(task.id, event.target.value)}>
                                                <option value="open">Открыта</option>
                                                <option value="in_progress">В работе</option>
                                                <option value="blocked">Блок</option>
                                                <option value="done">Готово</option>
                                            </select>
                                        </label>
                                        <button onClick={() => startEditTask(task)}><Pencil size={16} /> Редактировать</button>
                                    </div>
                                </div>

                                <div className="task-actions task-actions-legacy">
                                    {!task.archived && task.checklist?.length > 0 && (
                                        <button onClick={() => handleAcceptAllOk(task)}><CheckCircle2 size={16} /> Все ссылки ок + архив</button>
                                    )}
                                    <button onClick={() => startEditTask(task)}><Pencil size={16} /> Редактировать</button>
                                    {task.archived ? (
                                        <button onClick={() => handleArchiveChange(task.id, false)}><RotateCcw size={16} /> Вернуть</button>
                                    ) : (
                                        <button onClick={() => handleArchiveChange(task.id, true)}><Archive size={16} /> Принять в архив</button>
                                    )}
                                    <button onClick={() => handleStatusChange(task.id, "open")}>Открыта</button>
                                    <button onClick={() => handleStatusChange(task.id, "in_progress")}>В работу</button>
                                    <button onClick={() => handleStatusChange(task.id, "blocked")}>Блок</button>
                                    <button onClick={() => handleStatusChange(task.id, "done")}><CheckCircle2 size={16} /> Готово</button>
                                </div>
                            </article>
                        );
                    })}
                </section>
            </section>
        </main>
    );
};

export default SiteTasks;
